import React, { useState } from 'react';
import { userService } from '../services/api';
import { Button } from './ui';
import { X } from 'lucide-react';

const ActivityCompletionForm = ({ activity, onComplete, onClose }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    duration: activity?.estimatedDuration || 15,
    notes: '',
    mood: 5,
    energyLevel: 5,
    difficulty: 'moderate'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: isNaN(value) ? value : parseInt(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await userService.logActivityCompletion(activity._id, formData);
      
      setMessage({
        type: 'success',
        text: 'Activity logged successfully! Great work!'
      });

      // Call parent callback after a brief delay
      setTimeout(() => {
        onComplete();
      }, 1000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to log activity'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-neutral-900">Log Activity</h3>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {activity && (
          <div className="mb-4 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
            <p className="font-medium text-neutral-900">{activity.name}</p>
            <p className="text-sm text-neutral-600 mt-1">{activity.description}</p>
          </div>
        )}

        {message && (
          <div
            className={`mb-4 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Date
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Duration (minutes)
              </label>
              <input
                type="number"
                name="duration"
                min="1"
                max="240"
                value={formData.duration}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Difficulty
              </label>
              <select
                name="difficulty"
                value={formData.difficulty}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="very_easy">Very Easy</option>
                <option value="easy">Easy</option>
                <option value="moderate">Moderate</option>
                <option value="difficult">Difficult</option>
                <option value="very_difficult">Very Difficult</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              How did it affect your mood? {formData.mood}/10
            </label>
            <input
              type="range"
              name="mood"
              min="1"
              max="10"
              value={formData.mood}
              onChange={handleChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-neutral-500 mt-1">
              <span>Worse</span>
              <span>Better</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Energy level after? {formData.energyLevel}/10
            </label>
            <input
              type="range"
              name="energyLevel"
              min="1"
              max="10"
              value={formData.energyLevel}
              onChange={handleChange}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-neutral-500 mt-1">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="How did this activity feel? Any observations?"
              rows="3"
              className="w-full px-3 py-2 border border-neutral-300 rounded-soft focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              fullWidth
            >
              {loading ? 'Logging...' : 'Log Activity'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityCompletionForm;
